import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  User, Mail, Phone, Monitor, Smartphone, Lock, Clock, BadgeCheck,
  CalendarDays, LogOut, Sun, Moon, Laptop, Save,
  UserCog, KeyRound, ShieldCheck, MessageCircle, Headset, MapPin, ExternalLink, type LucideIcon,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { useThemeStore, type ThemeMode } from '../../store/theme';
import { FileUploadField } from '../../components/FileUploadField';
import { PhoneInput } from '../../components/PhoneInput';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { formatIst, formatIstDate } from '../../lib/time';
import { STATE_NAME } from '../../constants/indianStates';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import { Button, Card, Input, Select, Field, Tag, Avatar, Modal, Segmented, toast, cn } from '../../ui';
import { PALETTE } from '../../lib/palette';

const LANGS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिंदी (Hindi)' },
  { value: 'mr', label: 'मराठी (Marathi)' },
  { value: 'bn', label: 'বাংলা (Bengali)' },
  { value: 'or', label: 'ଓଡ଼ିଆ (Odia)' },
];

const THEMES: { key: ThemeMode; label: string; icon: LucideIcon }[] = [
  { key: 'light', label: 'Light', icon: Sun },
  { key: 'dark', label: 'Dark', icon: Moon },
  { key: 'system', label: 'System', icon: Laptop },
];

const STATUS: Record<string, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  suspended: { label: 'Suspended', cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  locked: { label: 'Locked', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Best-effort browser + OS from a user-agent string (web sessions). */
function parseUA(ua?: string | null): { browser: string; os: string } {
  if (!ua) return { browser: 'Web browser', os: '' };
  const os = /Windows/.test(ua) ? 'Windows'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad|iPod|iOS/.test(ua) ? 'iOS'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux' : '';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\/|Opera/.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari' : 'Web browser';
  return { browser, os };
}

type SessionRow = {
  id: string; jti: string; platform?: string | null; deviceModel?: string | null;
  appVersion?: string | null; ip?: string | null; userAgent?: string | null;
  createdAt: string; expiresAt: string; isCurrent: boolean;
};

function deviceInfo(s: SessionRow): { icon: JSX.Element; primary: string; secondary: string } {
  const isMobile = (!!s.platform && s.platform !== 'web') || !!s.deviceModel;
  if (isMobile) {
    const primary = s.deviceModel || (s.platform ? `${cap(s.platform)} device` : 'Mobile device');
    const secondary = [s.platform && cap(s.platform), s.appVersion && `app v${s.appVersion}`].filter(Boolean).join(' · ');
    return { icon: <Smartphone className="size-5" />, primary, secondary };
  }
  const { browser, os } = parseUA(s.userAgent);
  return { icon: <Monitor className="size-5" />, primary: [browser, os].filter(Boolean).join(' · ') || 'Web browser', secondary: '' };
}

function ReadOnly({ icon: Icon, label, value, hint, mono, action }: {
  icon: LucideIcon; label: string; value?: string | null; hint?: string; mono?: boolean; action?: ReactNode;
}) {
  return (
    <div className="rounded-control border border-line bg-surface-2/40 px-3.5 py-2.5">
      <div className="flex items-center gap-1.5 type-overline text-muted">
        <Icon className="size-3.5" /> <span className="flex-1">{label}</span>
        {action ?? <Lock className="size-3 text-subtle" />}
      </div>
      <div className={cn('mt-1 break-words type-body-medium text-content', mono && 'font-mono text-xs')}>
        {value || '—'}
      </div>
      {hint && <div className="mt-0.5 text-2xs text-muted">{hint}</div>}
    </div>
  );
}

const REP_ROLE_LABEL: Record<string, string> = {
  SALES_PERSON: 'Sales Representative',
  SALES_HEAD: 'Sales Head',
};

type RepCoverage = { level: string; name: string } | null;

/** Phrasing for the rep's coverage of THIS customer's area (the one territory on the customer's own
 *  chain that the rep owns) — "Maharashtra (statewide)", "Nanded district", or a local area name. */
function coverageText(cov: RepCoverage): string | null {
  if (!cov?.name) return null;
  const { level, name } = cov;
  if (level === 'state') return `${name} (statewide)`;
  if (level === 'district') return `${name} district`;
  return name; // beat = a local area
}

type MyRep = {
  rep: { id: string; name: string; role: string; mobile: string; photoUrl: string | null } | null;
  coverage: RepCoverage;
  company: { name: string; phone: string | null };
};

const telHref = (num: string) => { window.location.href = `tel:${num}`; };
const waOpen = (num: string) => window.open(`https://wa.me/${num.replace(/[^0-9]/g, '')}`, '_blank', 'noopener');

/** Customer-facing "who's my rep + how to reach them" card (retailer / farmer profile).
 *  Falls back to company support when no active rep is assigned. */
function RepCard({ data }: { data?: MyRep }) {
  const rep = data?.rep;
  const covText = coverageText(data?.coverage ?? null);
  const company = data?.company;
  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
      <div className="flex items-center gap-2 border-b border-line bg-surface-2/40 px-4 py-2.5 type-overline text-muted">
        <Headset className="size-3.5 text-brand-600" /> Your Sales Representative
      </div>
      <div className="p-4">
        {rep ? (
          <>
            <div className="flex items-center gap-3">
              <Avatar src={rep.photoUrl ?? undefined} size={48} fallback={<User className="size-6" />} className="bg-brand-600 text-white" />
              <div className="min-w-0">
                <div className="truncate font-semibold text-content">{rep.name}</div>
                <span className={cn(
                  'mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 type-caption-strong',
                  rep.role === 'SALES_HEAD'
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    : 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
                )}>
                  {REP_ROLE_LABEL[rep.role] ?? 'Representative'}
                </span>
              </div>
            </div>
            {covText && (
              <div className="mt-3 flex items-start gap-2 rounded-control bg-brand-50/60 px-3 py-2 text-sm text-content dark:bg-brand-900/20">
                <MapPin className="mt-0.5 size-4 shrink-0 text-brand-600" />
                <span><span className="text-muted">Covers </span>{covText}</span>
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button variant="default" size="sm" icon={<Phone className="size-4" />} onClick={() => telHref(rep.mobile)}>Call</Button>
              <Button variant="primary" size="sm" icon={<MessageCircle className="size-4" />} onClick={() => waOpen(rep.mobile)}>WhatsApp</Button>
            </div>
          </>
        ) : (
          <div className="text-center">
            <div className="mx-auto grid size-11 place-items-center rounded-full bg-surface-2 text-muted"><Headset className="size-5" /></div>
            <p className="mt-2 type-body-medium text-content">No representative assigned yet</p>
            {company?.phone ? (
              <>
                <p className="mt-0.5 text-xs text-muted">Reach {company.name} for any help.</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button variant="default" size="sm" icon={<Phone className="size-4" />} onClick={() => telHref(company.phone!)}>Call</Button>
                  <Button variant="primary" size="sm" icon={<MessageCircle className="size-4" />} onClick={() => waOpen(company.phone!)}>WhatsApp</Button>
                </div>
              </>
            ) : (
              <p className="mt-0.5 text-xs text-muted">Please contact the company for assistance.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Self-contained green map pin (Leaflet's default marker images don't resolve under bundlers).
const LOC_PIN = L.divIcon({
  className: '', iconSize: [28, 28], iconAnchor: [14, 28],
  html: `<svg viewBox="0 0 24 32" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill={PALETTE.brand[600]} stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white"/></svg>`,
});

// Leaflet can mount before the column has its final width — recompute once it settles.
function FixSize() {
  const map = useMap();
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 200); return () => clearTimeout(t); }, [map]);
  return null;
}

type MyLocation = { latitude: string | null; longitude: string | null; address: string | null; city: string | null; stateCode: string | null; pincode: string | null } | null;

/** The customer's own registered address + map pin (retailer / farmer profile). */
function LocationCard({ location }: { location?: MyLocation }) {
  const lat = Number(location?.latitude), lng = Number(location?.longitude);
  const hasPin = location?.latitude != null && location?.longitude != null && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
  const stateName = location?.stateCode ? (STATE_NAME.get(location.stateCode) ?? location.stateCode) : '';
  const place = [location?.address, location?.city, stateName, location?.pincode].filter(Boolean).join(', ');
  const gmaps = hasPin
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : (place ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}` : null);
  return (
    <Card
      title="Your location"
      extra={gmaps ? (
        <a href={gmaps} target="_blank" rel="noopener" className="inline-flex items-center gap-1 type-label-strong text-brand-600 hover:text-brand-700 dark:text-brand-400">
          <ExternalLink className="size-3.5" /> Open in Google Maps
        </a>
      ) : undefined}
    >
      {place && (
        <p className="mb-3 flex items-start gap-2 text-sm text-content">
          <MapPin className="mt-0.5 size-4 shrink-0 text-brand-600" /> <span>{place}</span>
        </p>
      )}
      {hasPin ? (
        <div className="overflow-hidden rounded-card border border-line">
          <MapContainer center={[lat, lng]} zoom={14} style={{ height: 300, width: '100%' }} scrollWheelZoom={false}>
            <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
            <FixSize />
            <Marker position={[lat, lng]} icon={LOC_PIN} />
          </MapContainer>
        </div>
      ) : (
        <div className="rounded-card border border-line bg-surface-2/40 px-4 py-6 text-center">
          <MapPin className="mx-auto size-6 text-subtle" />
          <p className="mt-2 text-sm text-muted">
            No map pin on your account yet{place ? <> for <b className="text-content">{place}</b></> : ''}.
          </p>
          <p className="mt-0.5 text-xs text-muted">Ask your representative to update your location.</p>
        </div>
      )}
    </Card>
  );
}

export function ProfilePage() {
  const qc = useQueryClient();
  const syncUser = useAuthStore((s) => s.syncUser);
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Super-admin self-change of login number
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newMobile, setNewMobile] = useState('');
  const [confirmMobile, setConfirmMobile] = useState('');

  // Editable form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [languagePref, setLanguagePref] = useState('en');
  const [photo, setPhoto] = useState('');
  const [contactPerson, setContactPerson] = useState(''); // retailer only — the person we deal with

  const { data: profile } = useQuery({
    queryKey: ['profile-me'],
    queryFn: () => request<any>({ url: '/profile/me' }),
  });

  /*
   * A PAGE of sessions plus the true total — never every row. The list used to be unpaged, and an
   * account that is signed into often (a Super Admin used by every test run) had 1,768 live sessions:
   * the card drew 2,294 buttons and stalled the browser (certification DEF-032). The title keeps the
   * real total; the list grows 20 at a time.
   */
  const [sessionLimit, setSessionLimit] = useState(20);
  const { data: sessionsPage } = useQuery({
    queryKey: ['profile-sessions', sessionLimit],
    queryFn: () => request<{ total: number; rows: SessionRow[] }>({ url: '/profile/sessions', params: { limit: sessionLimit } }),
  });
  const sessions = sessionsPage?.rows;

  // Retailers/farmers see who their assigned rep is + how to reach them.
  const { data: myRep } = useQuery({
    queryKey: ['profile-my-rep'],
    queryFn: () => request<MyRep>({ url: '/profile/my-rep' }),
    enabled: profile?.role?.code === 'RETAILER' || profile?.role?.code === 'FARMER',
  });

  useEffect(() => {
    if (profile) {
      setFullName(profile.fullName ?? '');
      setEmail(profile.email ?? '');
      setLanguagePref(profile.languagePref ?? 'en');
      setPhoto(profile.profilePhotoUrl ?? '');
      setContactPerson(profile.contactPerson ?? '');
    }
  }, [profile]);

  const dirty = useMemo(() => {
    if (!profile) return false;
    return (
      fullName !== (profile.fullName ?? '') ||
      email !== (profile.email ?? '') ||
      languagePref !== (profile.languagePref ?? 'en') ||
      photo !== (profile.profilePhotoUrl ?? '') ||
      contactPerson !== (profile.contactPerson ?? '')
    );
  }, [profile, fullName, email, languagePref, photo, contactPerson]);

  const save = useConfirmedMutation({ title: "Save your profile?", okText: "Yes, save" }, {
    mutationFn: () => request({
      url: '/profile/me', method: 'PUT',
      data: { fullName: fullName.trim(), email: email.trim() || null, languagePref, profilePhotoUrl: photo || null, ...(profile?.role?.code === 'RETAILER' ? { contactPerson: contactPerson.trim() } : {}) },
    }),
    onSuccess: () => {
      toast.success('Profile updated');
      // Reflect name/photo in the header immediately.
      syncUser({ fullName: fullName.trim(), profilePhotoUrl: photo || null });
      qc.invalidateQueries({ queryKey: ['profile-me'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Could not save profile'),
  });

  const revoke = useConfirmedMutation({ title: "Sign out this device?", content: "Whoever is using it is signed out immediately.", okText: "Yes, sign it out", danger: true }, {
    mutationFn: (jti: string) => request({ url: `/profile/sessions/${jti}`, method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Device signed out');
      qc.invalidateQueries({ queryKey: ['profile-sessions'] });
    },
    onError: () => toast.error('Could not revoke session'),
  });

  const revokeAll = useMutation({
    mutationFn: () => request({ url: '/profile/sessions/all/other', method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Other devices signed out');
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ['profile-sessions'] });
    },
    onError: () => toast.error('Could not sign out other devices'),
  });

  const changeMobile = useConfirmedMutation({ title: "Change your login number?", content: "Your sign-in OTP will go to the new number from now on. Make sure you can receive messages on it.", okText: "Yes, change it", danger: true }, {
    mutationFn: () => request({ url: '/profile/mobile', method: 'PUT', data: { mobile: newMobile.trim() } }),
    onSuccess: () => {
      toast.success('Login number updated');
      setMobileOpen(false);
      setNewMobile('');
      setConfirmMobile('');
      qc.invalidateQueries({ queryKey: ['profile-me'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Could not change number'),
  });

  if (!profile) return null;

  const status = STATUS[profile.status] ?? { label: cap(profile.status ?? ''), cls: 'bg-surface-2 text-muted' };
  const roleName = profile.role?.name ?? profile.role?.code ?? '—';
  // The TRUE number of live sessions, from the server — not the length of the page on screen.
  const sessionCount = sessionsPage?.total ?? 0;
  const isCustomer = profile.role?.code === 'RETAILER' || profile.role?.code === 'FARMER';
  const isRetailer = profile.role?.code === 'RETAILER';

  // Only the Super Admin may self-change their login number.
  const isSuperAdmin = profile.role?.code === 'SUPER_ADMIN';
  const mobileValid = !!newMobile && isValidPhoneNumber(newMobile, 'IN');
  const mobileMatch = newMobile.trim() !== '' && newMobile.trim() === confirmMobile.trim();

  // Cards are extracted so the two workbench columns can be assigned PER VIEW (staff vs customer)
  // for balanced heights — customers have two extra big cards (rep + map), so a fixed assignment
  // would leave one column short. Balancing avoids floating cards and dead space.
  const editProfileCard = (
    <Card title="Edit profile" extra={<span className="text-xs text-muted">These fields are yours to change</span>}>
      <form onSubmit={(e) => { e.preventDefault(); if (fullName.trim()) save.mutate(); }} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={isRetailer ? 'Business name' : 'Full name'} required>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={isRetailer ? 'Your shop / firm name' : 'Your name'} />
          </Field>
          {isRetailer && (
            <Field label="Contact person" hint="The person we deal with at your business.">
              <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} placeholder="e.g. Ramesh Kumar" />
            </Field>
          )}
          <Field label="Email" hint="Optional — used for notices & reports.">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
          </Field>
          <Field label="Preferred language" hint="Language for app text & WhatsApp messages.">
            <Select value={languagePref} onChange={setLanguagePref} options={LANGS} />
          </Field>
          <Field label="Profile photo" hint={isCustomer ? 'A photo for your account.' : 'Also your face-match enrolment photo for attendance.'}>
            <FileUploadField category="profile-photo" value={photo} onChange={setPhoto} buttonLabel="Upload photo" preview />
          </Field>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <Button variant="primary" type="submit" icon={<Save className="size-4" />} loading={save.isPending} disabled={!fullName.trim() || !dirty}>
            Save changes
          </Button>
          {dirty && <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>}
        </div>
      </form>
    </Card>
  );

  const signinCard = (
    <Card title="Sign-in method">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
          <KeyRound className="size-5" />
        </span>
        <div>
          <div className="font-semibold text-content">One-time passcode (OTP)</div>
          <p className="mt-0.5 text-sm text-muted">
            You sign in with a code sent to <strong className="text-content">{profile.mobile}</strong> over WhatsApp.
            There's no password to set or manage.
          </p>
        </div>
      </div>
    </Card>
  );

  const sessionsCard = (
    <Card
      title={`Active sessions (${sessionCount})`}
      extra={
        <Button variant="danger" size="sm" icon={<LogOut className="size-4" />} disabled={sessionCount <= 1} onClick={() => setConfirmOpen(true)}>
          Sign out other devices
        </Button>
      }
    >
      <div className="max-h-[26rem] space-y-2.5 overflow-y-auto pr-1">
        {(sessions ?? []).map((s) => {
          const d = deviceInfo(s);
          return (
            <div
              key={s.id}
              className={cn(
                'flex items-center gap-3 rounded-card border px-4 py-3',
                s.isCurrent ? 'border-brand-200 bg-brand-50/50 dark:border-brand-800 dark:bg-brand-900/20' : 'border-line bg-surface',
              )}
            >
              <span className={cn(
                'grid size-10 shrink-0 place-items-center rounded-full',
                s.isCurrent ? 'bg-brand-100 text-brand-700 dark:bg-brand-800/50 dark:text-brand-200' : 'bg-surface-2 text-muted',
              )}>
                {d.icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold text-content">{d.primary}</span>
                  {s.isCurrent && <Tag tone="green">This device</Tag>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted">
                  <span className="inline-flex items-center gap-1"><Clock className="size-3" /> {formatIst(s.createdAt)}</span>
                  {s.ip && <span>IP {s.ip}</span>}
                  {d.secondary && <span>{d.secondary}</span>}
                </div>
              </div>
              {s.isCurrent ? (
                <span className="shrink-0 type-label text-muted">Current</span>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  loading={revoke.isPending && revoke.variables === s.jti}
                  onClick={() => revoke.mutate(s.jti)}
                >
                  Revoke
                </Button>
              )}
            </div>
          );
        })}
        {sessionCount === 0 && <p className="text-sm text-muted">No active sessions.</p>}
        {sessionCount > (sessions?.length ?? 0) && (
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <span className="text-xs text-muted">
              Showing the {sessions?.length ?? 0} most recent of {sessionCount}.
              {sessionLimit >= 200 ? ' Sign out other devices to clear the rest.' : ''}
            </span>
            {sessionLimit < 200 && (
              <Button variant="default" size="sm" onClick={() => setSessionLimit((n) => Math.min(200, n + 20))}>
                Show 20 more
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );

  const accountCard = (
    <Card
      title="Account details"
      extra={<span className="inline-flex items-center gap-1 text-xs text-muted"><Lock className="size-3" /> Managed by your administrator</span>}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <ReadOnly
          icon={Phone}
          label="Mobile (login ID)"
          value={profile.mobile}
          hint={isSuperAdmin ? 'Your sign-in number — changing it applies from your next login.' : 'Your sign-in number — ask an admin to change it.'}
          action={isSuperAdmin ? (
            <button
              type="button"
              onClick={() => { setNewMobile(''); setConfirmMobile(''); setMobileOpen(true); }}
              className="type-overline text-brand-600 hover:text-brand-700 dark:text-brand-400"
            >
              Change
            </button>
          ) : undefined}
        />
        <ReadOnly icon={ShieldCheck} label="Role" value={roleName} />
        <ReadOnly icon={BadgeCheck} label="Account status" value={status.label} />
        {profile.manager && <ReadOnly icon={UserCog} label="Reports to" value={profile.manager.name} />}
        <ReadOnly icon={CalendarDays} label="Member since" value={formatIstDate(profile.createdAt)} />
        <ReadOnly icon={Clock} label="Last login" value={profile.lastLoginAt ? formatIst(profile.lastLoginAt) : 'Never'} />
      </div>
    </Card>
  );

  const appearanceCard = (
    <Card title="Appearance">
      <Field label="Theme" hint="How the app looks on this device.">
        <Segmented
          value={mode}
          onChange={(v) => setMode(v as ThemeMode)}
          options={THEMES.map((t) => ({ value: t.key, label: t.label, icon: <t.icon /> }))}
        />
      </Field>
    </Card>
  );

  // The Notifications card and the settings page behind it are gone: how an event reaches someone is
  // Green Bay's decision, not a per-person setting, so there is nothing here for anyone to manage.
  // Notifications themselves are unchanged — they arrive in the bell in the top bar.

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 pb-10 sm:px-6 lg:px-8">
      <h1 className="type-page-title text-content">My Profile</h1>
      <p className="mt-1 text-sm text-muted">Manage your account, security and preferences.</p>

      {/* ── Identity masthead (full-width horizontal band) ── */}
      <div className="mt-6 overflow-hidden rounded-card border border-line bg-surface shadow-e1">
        <div className="h-16 bg-gradient-to-r from-brand-500 to-brand-700 sm:h-20" />
        <div className="flex flex-col gap-4 px-6 pb-6 sm:flex-row sm:items-center sm:gap-5">
          <div className="-mt-12 flex flex-col items-center gap-3 text-center sm:-mt-6 sm:flex-row sm:items-center sm:gap-4 sm:text-left">
            <Avatar
              src={profile.profilePhotoUrl}
              size={96}
              fallback={<User className="size-12" />}
              className="bg-brand-600 text-white shadow-soft ring-4 ring-surface"
            />
            <div className="min-w-0 sm:pt-3">
              {/* Sits under the page's own <h1> "My Profile" — at page-title size the two headings
                  read as equals and the hierarchy collapsed. */}
              <h2 className="type-section-title text-content">{profile.fullName}</h2>
              <div className="mt-1.5 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <Tag tone="blue">{roleName}</Tag>
                <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 type-label-strong', status.cls)}>
                  <BadgeCheck className="size-3.5" /> {status.label}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:ml-auto sm:items-center sm:justify-end sm:pt-3">
            <span className="inline-flex items-center gap-1.5 rounded-control bg-surface-2/60 px-3 py-1.5 text-sm text-content">
              <Phone className="size-4 shrink-0 text-muted" /> {profile.mobile}
            </span>
            {profile.email && (
              <span className="inline-flex max-w-[240px] items-center gap-1.5 rounded-control bg-surface-2/60 px-3 py-1.5 text-sm text-content">
                <Mail className="size-4 shrink-0 text-muted" /> <span className="truncate">{profile.email}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-control bg-surface-2/60 px-3 py-1.5 text-sm text-content">
              <CalendarDays className="size-4 shrink-0 text-muted" /> <span className="text-muted">Joined</span> {formatIstDate(profile.createdAt)}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-control bg-surface-2/60 px-3 py-1.5 text-sm text-content">
              <Clock className="size-4 shrink-0 text-muted" /> <span className="text-muted">Last login</span> {profile.lastLoginAt ? formatIstDate(profile.lastLoginAt) : 'Never'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Workbench: two columns, assigned per view so both stay balanced (no floating cards) ── */}
      <div className="mt-5 grid items-start gap-5 lg:grid-cols-2">
        <div className="min-w-0 space-y-5">
          {editProfileCard}
          {isCustomer ? (
            <>
              {signinCard}
              {sessionsCard}
              {appearanceCard}
            </>
          ) : (
            <>
              {accountCard}
              {appearanceCard}
            </>
          )}
        </div>
        <div className="min-w-0 space-y-5">
          {isCustomer ? (
            <>
              <RepCard data={myRep} />
              {accountCard}
              {/* Location sits directly under Account details, contained (not full-width) */}
              <LocationCard location={profile.location} />
            </>
          ) : (
            <>
              {signinCard}
              {sessionsCard}
            </>
          )}
        </div>
      </div>

      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Sign out other devices?"
        description="Every device except this one will be signed out immediately."
        width={440}
        footer={
          <>
            <Button variant="default" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="danger" loading={revokeAll.isPending} onClick={() => revokeAll.mutate()}>Sign out others</Button>
          </>
        }
      >
        <p className="text-sm text-muted">Use this if you've signed in on a shared, lost or old device.</p>
      </Modal>

      <Modal
        open={mobileOpen}
        onOpenChange={setMobileOpen}
        title="Change login number"
        description="You'll use this number to receive your sign-in OTP from now on."
        width={460}
        footer={
          <>
            <Button variant="default" onClick={() => setMobileOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={changeMobile.isPending} disabled={!mobileValid || !mobileMatch} onClick={() => changeMobile.mutate()}>
              Update number
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="rounded-control border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
            Make sure you can receive WhatsApp on the new number — your next login OTP goes there.
          </div>
          <Field label="Current number">
            <Input value={profile.mobile} disabled />
          </Field>
          <Field label="New number" required error={newMobile.trim() && !mobileValid ? 'Enter a valid phone number' : undefined}>
            <PhoneInput value={newMobile} onChange={(v) => setNewMobile(v ?? '')} placeholder="New number" />
          </Field>
          <Field label="Confirm new number" required error={confirmMobile.trim() && confirmMobile.trim() !== newMobile.trim() ? "Numbers don't match" : undefined}>
            <PhoneInput value={confirmMobile} onChange={(v) => setConfirmMobile(v ?? '')} placeholder="Re-enter number" />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
